import { useDefaultSorting, SelectOptionType, Table, Switch, Select, GroupsIcon } from '@bp/ui-components';
import { FragmentType } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { UseGroupsFragment, useGroups } from '../hooks/useGroups';
import { GroupTableType } from '../types/types';
import styles from './_GroupsTable.module.scss';

type GroupsStudentTableProps = {
  query: FragmentType<typeof UseGroupsFragment> | undefined;
  yearUuid: string;
  showSpanning: boolean;
  onYearSelect: (yearUuid: string) => void;
  onToggleSpanning: () => void;
  onNavigate: (uuid: string) => void;
};

export const GroupsStudentTable = ({
  query,
  yearUuid,
  showSpanning,
  onYearSelect,
  onToggleSpanning,
  onNavigate,
}: GroupsStudentTableProps) => {
  const { t } = useTranslation();
  const { schoolyears } = useContext(OrganizationConfigContext);
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();

  const { data, columns } = useGroups({
    query,
    columns: ['name', 'schoolYear'],
    onNavigate,
  });

  const { sorting, onSortingChange } = useDefaultSorting();

  const schoolYearOptions: SelectOptionType[] =
    schoolyears?.map(({ name, uuid }) => {
      return {
        label: name,
        value: uuid,
      };
    }) ?? [];

  return (
    <Table<GroupTableType>
      className={styles['groups-table']}
      data={data}
      columns={columns}
      breakpoint='580px'
      canScroll
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      showActionBar
      actionBarSettings={{
        showExpertFilter: true,
        extendedActionsRight: (
          <div className={styles.actions}>
            <Switch
              name='toggle-spanning'
              checked={showSpanning}
              onChange={onToggleSpanning}
              label={t('groups.showSchoolyearSpanning')}
              className={styles.switch}
            />
            <Select
              value={schoolYearOptions.find((y) => y.value === yearUuid)}
              options={schoolYearOptions}
              onChange={(option) => {
                onYearSelect((option as SingleValue<SelectOptionType>)?.value as string);
              }}
              name='school-year-select'
              dense
              className={styles.select}
            />
          </div>
        ),
      }}
      isOnWhite={false}
      showBorderRadius
      showShadow
      emptyStateSettings={{ icon: <GroupsIcon />, padding: 'xl' }}
    />
  );
};
